/**
 * LivePreview — floating preview panel for Display Engine admin
 * - Iframe of /displayboard/{slug} updates in real time via WebSocket (board's own WS reconnects on theme/items changes)
 * - QR code generator (qrcode.react) — shop owner scans on TV/phone
 * - Device toggle: TV landscape (16:9) | Tablet (4:3) | Mobile portrait (9:16)
 * - Cast / Open / Refresh actions
 */
import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Monitor, Tablet, Smartphone, Cast, ExternalLink, RefreshCw, QrCode, X,
  Maximize2, Minimize2,
} from 'lucide-react';

const DEVICE_PRESETS = {
  tv:     { label: 'TV',     icon: Monitor,    w: 640, h: 360, name: '16:9 TV / Landscape' },
  tablet: { label: 'Tablet', icon: Tablet,     w: 480, h: 360, name: '4:3 Tablet' },
  mobile: { label: 'Mobile', icon: Smartphone, w: 270, h: 480, name: '9:16 Mobile Portrait' },
};

export default function LivePreview({ slug, clientName, themeKey }) {
  const [open, setOpen] = useState(true);
  const [device, setDevice] = useState('tv');
  const [showQr, setShowQr] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);

  const boardPath = `/displayboard/${slug}`;
  const boardUrl = window.location.origin + boardPath;
  const preset = DEVICE_PRESETS[device];

  const refresh = () => setReloadKey(k => k + 1);
  const openInNew = () => window.open(boardPath, '_blank', 'noopener,noreferrer');
  const goFullscreen = () => {
    try {
      iframeRef.current?.requestFullscreen?.();
    } catch {}
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-30 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl shadow-2xl flex items-center gap-2 font-semibold hover:scale-105 transition"
        data-testid="live-preview-toggle">
        <Monitor className="w-5 h-5" /> Live Preview
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-30 bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl backdrop-blur p-3 max-w-[calc(100vw-2rem)]" data-testid="live-preview-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-bold text-white">Live Preview</span>
          <span className="text-xs text-slate-400 hidden sm:inline">· {clientName}</span>
        </div>
        <div className="flex items-center gap-1">
          {Object.entries(DEVICE_PRESETS).map(([k, p]) => {
            const Icon = p.icon;
            return (
              <button key={k} onClick={() => setDevice(k)}
                title={p.name}
                className={`p-1.5 rounded ${device === k ? 'bg-amber-500/30 text-amber-300' : 'text-slate-400 hover:bg-white/10'}`}
                data-testid={`preview-device-${k}`}>
                <Icon className="w-3.5 h-3.5" />
              </button>
            );
          })}
          <div className="w-px h-5 bg-white/10 mx-1" />
          <button onClick={refresh} title="Refresh" className="p-1.5 text-slate-400 hover:bg-white/10 rounded" data-testid="preview-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setShowQr(!showQr)} title="Show QR" className={`p-1.5 rounded ${showQr ? 'bg-amber-500/30 text-amber-300' : 'text-slate-400 hover:bg-white/10'}`} data-testid="preview-qr-toggle">
            <QrCode className="w-3.5 h-3.5" />
          </button>
          <button onClick={openInNew} title="Open in new tab" className="p-1.5 text-slate-400 hover:bg-white/10 rounded" data-testid="preview-open-new">
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
          <button onClick={goFullscreen} title="Fullscreen / Cast" className="p-1.5 text-slate-400 hover:bg-white/10 rounded" data-testid="preview-fullscreen">
            <Cast className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setOpen(false)} title="Minimize" className="p-1.5 text-slate-400 hover:bg-white/10 rounded" data-testid="preview-minimize">
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Preview body */}
      <div className="flex gap-2 items-start">
        <div className="rounded-xl overflow-hidden bg-black border border-white/10" style={{ width: preset.w, height: preset.h }} data-testid="preview-frame">
          <iframe
            key={`${slug}-${themeKey || 0}-${reloadKey}`}
            ref={iframeRef}
            src={boardPath}
            title={`Preview: ${clientName}`}
            className="w-full h-full"
            style={{ border: 0 }}
            data-testid="preview-iframe"
          />
        </div>

        {/* QR side panel */}
        {showQr && (
          <div className="bg-white p-3 rounded-xl flex flex-col items-center gap-2" data-testid="preview-qr">
            <QRCodeSVG value={boardUrl} size={140} level="M" includeMargin={false} />
            <div className="text-[10px] text-slate-700 font-mono break-all max-w-[140px] text-center">{boardUrl}</div>
            <button onClick={async () => {
              try {
                if (navigator.clipboard && window.isSecureContext) {
                  await navigator.clipboard.writeText(boardUrl);
                } else {
                  // Fallback for insecure / non-clipboard contexts
                  const ta = document.createElement('textarea');
                  ta.value = boardUrl;
                  ta.style.position = 'fixed'; ta.style.opacity = '0';
                  document.body.appendChild(ta); ta.select();
                  try { document.execCommand('copy'); } catch {}
                  document.body.removeChild(ta);
                }
              } catch { /* swallow — fallback already attempted */ }
            }}
              className="text-xs px-2 py-1 bg-slate-800 text-white rounded hover:bg-slate-700"
              data-testid="preview-copy-url">Copy URL</button>
          </div>
        )}
      </div>

      <div className="mt-2 text-[10px] text-slate-500 text-center">
        Edits to items / theme update this preview in real time via WebSocket.
      </div>
    </div>
  );
}
